import React, { createContext, ReactChild, useContext, useEffect, useState } from "react";
import { useScreenClass } from "@vp/swan";
import { BffDebugData } from "../types/BFFServiceTypes";
import { AttributeConstraint } from "../types/ConstraintsTypes";
import RangeType from "../types/RangeTypes";
import { SiteflowResponse } from "../types/ContentfulTypes";
import { LowestPrice } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { ProductContext } from "./ProductContextProvider";
import { getUpdatedState } from "../state/updatedState";
import { DebugContext } from "../components/debug/DebugContextProvider";
import { getPricingContextEncodedString } from "../utils/pricingContext";
import { MpvProvider } from "../utils/MpvProvider";
import { getScreenClass } from "../utils/screenClass";
import { PageStaticDataContext } from "./PageStaticDataContextProvider";
import { getMultiNextStep } from "../services/siteflow";
// @ts-ignore
import { ensureUserTestId } from "@vp/ab-test-cookie";
import useGetStudioDocumentUrl from "../hooks/useGetStudioDocumentUrl";
import { useExperimentsContext } from "../contexts/ExperimentsProvider";
import { RevisedDesignFirstFlowContext } from "../utils/abTests/abTests";
import { GalleryTemplateContext } from "./GalleryTemplateProvider";

export interface BffDataContextType {
  multisiteflow?: { flows: SiteflowResponse[] };
  compatibleValues: Record<string, string[]>;
  compatibleRanges: Record<string, RangeType[]>;
  compatibleQuantities: number[];
  attributeConstraints: AttributeConstraint[];
  prices: Record<string, LowestPrice>;
  temporarilyOutOfStock: boolean;
  initialPrices?: Record<string, LowestPrice>;
  debugData?: {
    selections: Record<string, string>;
    debugData: BffDebugData;
  }[];
}

export const BffDataContext = createContext<BffDataContextType>({
  compatibleValues: {},
  compatibleRanges: {},
  compatibleQuantities: [],
  attributeConstraints: [],
  prices: {},
  temporarilyOutOfStock: false,
});

export interface BffDataContextProviderProps {
  children?: ReactChild | ReactChild[] | undefined | null;
}

const BffDataContextProvider = (props: BffDataContextProviderProps) => {
  const {
    selections,
    selectedQuantity,
    clientSideStateInitialized,
    setClientSideStateInitialized,
    selectQuantity,
    setUpdateInProgress,
    updateInProgress,
  } = useContext(ProductContext);
  const { locale, mpv, tenant, ssrQuantities } = useContext(PageStaticDataContext);
  const { templateToken, previewUrl } = useContext(GalleryTemplateContext);
  const { isDebug } = useContext(DebugContext);
  const { isMobile } = getScreenClass(useScreenClass());
  const { studioDocumentUrl } = useGetStudioDocumentUrl();
  const mpvProvider = new MpvProvider(mpv);
  const { useExperiment } = useExperimentsContext();
  const revisedDesignFirstFlowVariation = useExperiment(RevisedDesignFirstFlowContext);
  const [bffDataState, setBffDataState] = useState({
    compatibleValues: mpvProvider.getAllOptionsAndValues(),
    compatibleRanges: {},
    compatibleQuantities: ssrQuantities.length > 0 ? ssrQuantities : [mpv.quantities.default ?? 0],
    multisiteflow: undefined,
    attributeConstraints: [],
    prices: {},
    temporarilyOutOfStock: false,
    debugData: undefined,
  } as BffDataContextType);

  useEffect(() => {
    let mounted = true;

    const getUpdatedBffData = async () => {
      if (selections) {
        const updatedBffData = await getUpdatedState({
          locale,
          currentCompatibleValues: bffDataState.compatibleValues,
          currentCompatibleRanges: bffDataState.compatibleRanges,
          selectedQuantity,
          mpv,
          selections,
          pricingContext: getPricingContextEncodedString(),
          isDebug,
        });

        const multisiteflow = await getMultiNextStep({
          tenant,
          culture: locale,
          productKey: mpv.coreProductId,
          version: mpv.version,
          optionValues: selections,
          quantity: updatedBffData.selectedQuantity,
          optimizelyEndUserId: ensureUserTestId(),
          isMobile,
          urlTokens: {
            documentUrl: studioDocumentUrl,
            ...(revisedDesignFirstFlowVariation === "test"
              ? { designTemplate: templateToken, previewInstructionsUrl: previewUrl }
              : {}),
          },
          mpvId: mpv.mpvId,
        });

        const newDebugData =
          isDebug && updatedBffData.debugData
            ? {
                selections,
                debugData: updatedBffData.debugData,
              }
            : undefined;

        if (mounted) {
          const newBffDataState = {
            initialPrices: bffDataState.initialPrices,
            compatibleValues: updatedBffData.compatibleValues,
            compatibleRanges: updatedBffData.compatibleRanges,
            compatibleQuantities: updatedBffData.compatibleQuantities,
            prices: updatedBffData.prices,
            multisiteflow: multisiteflow,
            attributeConstraints: updatedBffData.attributeConstraints || [],
            temporarilyOutOfStock: !!updatedBffData.temporarilyOutOfStock,
            debugData: newDebugData
              ? [...(bffDataState.debugData || []), newDebugData]
              : [...(bffDataState.debugData || [])],
          };
          if (!newBffDataState.initialPrices) {
            newBffDataState.initialPrices = updatedBffData.prices;
          }

          setBffDataState(newBffDataState);

          if (updatedBffData.selectedQuantity !== selectedQuantity) {
            selectQuantity(updatedBffData.selectedQuantity);
          }

          setUpdateInProgress(false);

          if (!clientSideStateInitialized) {
            setClientSideStateInitialized(true);
          }
        }
      }
    };

    if (updateInProgress) {
      getUpdatedBffData();
    }
    return () => {
      mounted = false;
    };
  }, [selections, selectedQuantity, updateInProgress, templateToken, previewUrl]);

  return (
    <BffDataContext.Provider value={{ ...bffDataState }}>{props.children}</BffDataContext.Provider>
  );
};

export default BffDataContextProvider;
